import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotifications } from '../../../contexts/notification';
import { mockMajorCourses } from '../../../data/mock/majorCourses.jsx';
import { 
  CardWithHeader, 
  Card 
} from '../../../styles/components/common/cards';
import { 
  Input, 
  FormGroup, 
  Label,
  Checkbox,
  Select
} from '../../../styles/components/common/forms';
import { 
  Button, 
  SecondaryButton
} from '../../../styles/components/common/buttons';
import { 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalFooter,
  ModalTitle,
  CloseButton 
} from '../../../styles/components/common/modals';
import { glassDeep, glassMedium } from '../../../styles/components/common/glassmorphism';

const CourseSelectionContainer = styled(ModalContent)`
  max-width: 900px;
  width: 85%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-height: 85vh;
  height: auto;
  overflow-y: auto;
  padding: 2rem;
  margin: 0 auto;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.3);
  animation: modalAppear 0.3s ease-out;
  
  @keyframes modalAppear {
    from { 
      opacity: 0;
      transform: translateY(-30px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    width: 95%;
    padding: 1.5rem;
  }
  
  /* Add custom scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.6);
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.8);
  }
`;

const CoursesList = styled.div`
  max-height: 50vh;
  min-height: 250px;
  overflow-y: auto !important;
  margin: 1.5rem 0;
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.2);
  transition: all 0.3s ease;
  position: relative;
  
  &:focus-within {
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    max-height: 40vh;
  }
  
  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.7);
  }
`;

const CourseItem = styled.div`
  ${glassMedium}
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  transition: all 0.25s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: transparent;
    transition: background 0.2s ease;
  }
  
  &:hover {
    background: rgba(15, 23, 42, 0.5);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  ${props => props.selected && `
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.4);
    
    &::before {
      background: rgba(99, 102, 241, 0.8);
    }
    
    &:hover {
      background: rgba(99, 102, 241, 0.25);
    }
  `}
`;

const CourseDetails = styled.div`
  flex: 1;
  margin-left: 1rem;
`;

const CourseTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
`;

const CourseDescription = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
`;

const CourseBadge = styled.span`
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  margin-left: 0.5rem;
  background: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
`;

const SearchBar = styled(FormGroup)`
  margin-bottom: 1.5rem;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(15, 23, 42, 0.4)'};
  color: ${props => props.active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 20px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    transform: translateY(-1px);
  }
`;

const CoursesCount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.5rem;
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
  }
  
  span {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
  }
`;

const getCourseCategory = (course) => {
  if (!course || !course.id) return 'Other';
  
  const match = course.id.match(/\d+/);
  if (!match) return 'Other';
  
  const courseNum = parseInt(match[0]);
  
  if (courseNum < 200) {
    return 'Introductory';
  } else if (courseNum >= 300) {
    return 'Advanced';
  } else {
    return 'Intermediate';
  }
};

const MajorCoursesSelection = ({ studentMajor, selectedCourses = [], onSave, onClose, internship = {} }) => {
  const { addNotification } = useNotifications();
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState(selectedCourses.map(course => course.id));
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courses] = useState(mockMajorCourses);
  const availableMajors = Object.keys(mockMajorCourses);
  
  // Log available majors to help with debugging
  console.log('Available majors:', availableMajors);
  console.log('Student major from props:', studentMajor);
  
  // Initialize with the student's major if provided, otherwise use the first available major
  const [selectedMajor, setSelectedMajor] = useState(() => {
    if (studentMajor && availableMajors.includes(studentMajor)) {
      return studentMajor;
    } else if (availableMajors.length > 0) {
      return availableMajors[0];
    }
    return '';
  });
  
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Define course categories
  const courseCategories = {
    all: 'All Courses',
    introductory: 'Introductory Courses',
    advanced: 'Advanced Courses',
    technical: 'Technical Skills',
    professional: 'Professional Skills'
  };
  
  useEffect(() => {
    // Set initial major if provided and it's changed externally
    if (studentMajor && studentMajor !== selectedMajor && availableMajors.includes(studentMajor)) {
      console.log('Updating selected major from props:', studentMajor);
      setSelectedMajor(studentMajor);
    }
  }, [studentMajor, selectedMajor, availableMajors]);
  
  useEffect(() => {
    // Fetch courses based on selected major
    // In a real app, this would be an API call
    const majorCourses = courses[selectedMajor] || [];
    console.log('Selected major:', selectedMajor);
    console.log('Available courses:', majorCourses.length);
    setAvailableCourses(majorCourses);
    // Reset filter when major changes
    setActiveFilter('all');
  }, [selectedMajor, courses]);
  
  const handleMajorChange = (e) => {
    setSelectedMajor(e.target.value);
    setSelectedCourseIds([]);
  };
  
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const filteredCourses = availableCourses.filter(course => {
    if (!course) return false;
    
    // Apply search term filter
    const matchesSearch = 
      (course.name && course.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (course.id && course.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Apply category filter
    if (activeFilter === 'all') {
      return matchesSearch;
    } else if (activeFilter === 'introductory') {
      // Course IDs that start with number 1 are introductory
      const match = course.id && course.id.match(/\d+/);
      return matchesSearch && match && match[0].startsWith('1');
    } else if (activeFilter === 'advanced') {
      // Course IDs with numbers 3 or higher are advanced
      const match = course.id && course.id.match(/\d+/);
      const courseNum = match ? parseInt(match[0]) : 0;
      return matchesSearch && courseNum >= 300;
    } else if (activeFilter === 'technical') {
      return matchesSearch && (
        (course.description && course.description.toLowerCase().includes('technical')) || 
        (course.description && course.description.toLowerCase().includes('programming')) || 
        (course.description && course.description.toLowerCase().includes('implementation')) ||
        (course.description && course.description.toLowerCase().includes('systems')) ||
        (course.description && course.description.toLowerCase().includes('design')) ||
        (course.name && course.name.toLowerCase().includes('algorithm')) ||
        (course.name && course.name.toLowerCase().includes('programming')) ||
        (course.name && course.name.toLowerCase().includes('database')) ||
        (course.name && course.name.toLowerCase().includes('network'))
      );
    } else if (activeFilter === 'professional') {
      return matchesSearch && (
        (course.description && course.description.toLowerCase().includes('communication')) || 
        (course.description && course.description.toLowerCase().includes('management')) ||
        (course.description && course.description.toLowerCase().includes('professional')) ||
        (course.description && course.description.toLowerCase().includes('team')) ||
        (course.name && course.name.toLowerCase().includes('business')) ||
        (course.name && course.name.toLowerCase().includes('communication')) ||
        (course.name && course.name.toLowerCase().includes('management'))
      );
    }
    
    return matchesSearch;
  });
  
  // Log filtered courses when they change
  useEffect(() => {
    console.log(`Filtered courses (${activeFilter}):`, filteredCourses.length);
  }, [filteredCourses, activeFilter]);
  
  const handleCourseSelection = (courseId) => {
    setSelectedCourseIds(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    // Get the full course objects for the selected IDs
    const coursesToSave = availableCourses.filter(course => selectedCourseIds.includes(course.id));
    
    // Simulate API call delay
    setTimeout(() => {
      onSave(coursesToSave);
      setIsSubmitting(false);
      
      const companyName = internship.company && (typeof internship.company === 'string' 
        ? internship.company 
        : internship.company.name);
      
      addNotification({
        title: "Courses Saved",
        message: `Your helpful courses selection${companyName ? ` for the ${companyName} internship` : ''} has been saved.`,
        type: "success",
      });
      
      if (onClose) onClose();
    }, 1000);
  };
  
  return (
    <ModalOverlay onClick={onClose}>
      <CourseSelectionContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Select Helpful Courses {selectedMajor ? `- ${selectedMajor}` : ''}</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
        </ModalHeader>

        <Card style={{ overflowY: 'visible' }}>
          <h3>Select courses that helped during your internship{internship.company ? ` at ${
            typeof internship.company === 'string' ? internship.company : internship.company.name
          }` : ''}</h3>
          <p>
            Please select the courses from your major that provided knowledge or skills that were helpful during your internship.
            This information will help future students prepare for similar opportunities.
          </p>
          
          <FormGroup>
            <Label htmlFor="major-select">Select Major</Label>
            <Select
              id="major-select"
              value={selectedMajor}
              onChange={handleMajorChange}
            >
              <option value="" disabled>Select your major</option>
              {availableMajors.map(major => (
                <option key={major} value={major}>{major}</option>
              ))}
            </Select>
          </FormGroup>

          <SearchBar>
            <Input
              type="text"
              placeholder="Search courses by name, code, or description..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </SearchBar>

          <FilterGroup>
            {Object.entries(courseCategories).map(([key, label]) => (
              <FilterButton
                key={key}
                active={activeFilter === key}
                onClick={() => handleFilterChange(key)}
              >
                {label}
              </FilterButton>
            ))}
          </FilterGroup>

          {availableCourses.length === 0 ? (
            <div style={{textAlign: 'center', padding: '2rem 0'}}>
              <p>No courses available for {selectedMajor || 'your major'}. Please contact your academic advisor.</p>
            </div>
          ) : (
            <>
              <h4>Available Courses ({filteredCourses.length})</h4>
              
              {filteredCourses.length === 0 ? (
                <p>No courses match your search criteria.</p>
              ) : (
                <CoursesList id="courses-list" style={{ overflowY: 'auto' }}>
                  {filteredCourses.map(course => (
                    <CourseItem 
                      key={course.id}
                      selected={selectedCourseIds.includes(course.id)}
                      onClick={() => handleCourseSelection(course.id)}
                    >
                      <Checkbox
                        id={`course-${course.id}`}
                        checked={selectedCourseIds.includes(course.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCourseSelection(course.id);
                        }}
                      />
                      <CourseDetails>
                        <label htmlFor={`course-${course.id}`} onClick={(e) => e.stopPropagation()}>
                          <CourseTitle>
                            {course.id}: {course.name}
                            <CourseBadge>{getCourseCategory(course)}</CourseBadge>
                          </CourseTitle>
                        </label>
                        <CourseDescription>{course.description}</CourseDescription>
                      </CourseDetails>
                    </CourseItem>
                  ))}
                </CoursesList>
              )}
              
              <CoursesCount>
                <p><span>Selected Courses:</span> {selectedCourseIds.length}</p>
              </CoursesCount>
            </>
          )}
        </Card>
        
        <ModalFooter>
          <SecondaryButton
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </SecondaryButton>
          <Button 
            type="button" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Selections'}
          </Button>
        </ModalFooter>
      </CourseSelectionContainer>
    </ModalOverlay>
  );
};

export default MajorCoursesSelection;